import * as React from 'react'
import { View, StyleSheet, ActivityIndicator, StyleProp } from 'react-native'
import { AppNavigation } from '../../../../navigation/types'
import {
  useInboundOutboundContributionsQuery,
  useUserTotalContributionsQuery,
  useUserTotalPerspectivesQuery,
} from '../../../../generated/graphql'
import { useTranslation } from 'react-i18next'
import { useNavigation } from '@react-navigation/native'
import { TitledTable } from '../../../../@ui/containers/others/title-table/titled-table'
import { DataTable, Text, TouchableRipple } from 'react-native-paper'
import SingleCell from '../../../../@ui/containers/others/title-table/single.cell'
import { useMemo } from 'react'
import { useTheme } from '../../../../hooks/theme.hook'
import { Icon } from '../../../../@ui/typo/icon'
import {
  Icon3dChallenge,
  Icon3dEdit,
  Icon3dInquiry,
  IconContributions,
  IconPerspective,
} from '../../../../utils/meldd-icons'
import { ViewStyle } from 'react-native'
import { useAuthContext } from '../../../../contexts/auth-context'
import { CoRankLabel } from '../../../../@ui/buttons/corank.button'
import { Row } from '../../../../@ui/containers/grid/row'

interface UserContributionsProps {
  userId: string
  style?: StyleProp<ViewStyle>
}

export function UserContributions(props: UserContributionsProps) {
  const { userId } = props
  const { t } = useTranslation('contributions')
  const { authAction } = useAuthContext()
  const theme = useTheme()
  const navigation = useNavigation<AppNavigation>()
  const {
    loading: loading,
    error,
    data: data,
  } = useInboundOutboundContributionsQuery({ variables: { userId } })
  const { data: totalContributionsData } = useUserTotalContributionsQuery({
    variables: { userId },
    skip: !userId,
  })
  const { data: totalPerspectivesData } = useUserTotalPerspectivesQuery({
    variables: { userId },
    skip: !userId,
  })

  const inboundOutbound = data?.inboundOutboundContributions
  const totalContributions = totalContributionsData?.userTotalContributions ?? 0
  const totalPerspectives = totalPerspectivesData?.userTotalPerspectives ?? 0
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {},
        typeCol: {
          flex: 2,
          alignItems: 'center',
        },
        row: { paddingRight: 0 },

        valueCol: {
          alignItems: 'center',
          flex: 0.66,
          justifyContent: 'center',
          paddingHorizontal: 20,
        },
        inquiry: {
          backgroundColor: theme.colors.inquiry50,
        },
        challenge: {
          backgroundColor: theme.colors.challenge50,
        },
        edit: {
          backgroundColor: theme.colors.edit50,
        },
        statItem: {
          flexDirection: 'row',
          alignItems: 'center',
          columnGap: 4,
        },
      }),
    [theme]
  )

  const content = useMemo(() => {
    if (loading && !inboundOutbound) return <SingleCell message={''} />
    if (error)
      return (
        <SingleCell key="0" message={t('Failed to load user contributions')} />
      )

    const { inbound, outbound } = inboundOutbound!
    return (
      <>
        <DataTable.Row style={styles.row} pointerEvents={'box-none'}>
          <DataTable.Cell style={styles.typeCol}>
            {t('Inbound A/R')}
          </DataTable.Cell>
          <TouchableRipple
            style={[
              styles.valueCol,
              { backgroundColor: theme.colors.inquiry50 },
            ]}
            onPress={() => {
              authAction(navigation, () =>
                navigation.navigate('UserContributions', {
                  contributions: inbound.inquiry.contributions,
                  title: t('Inbound A/R Inquiry'),
                })
              )
            }}
          >
            <Text>{inbound.inquiry.percentage}</Text>
          </TouchableRipple>
          <TouchableRipple
            style={[
              styles.valueCol,
              { backgroundColor: theme.colors.challenge50 },
            ]}
            onPress={() => {
              authAction(navigation, () =>
                navigation.navigate('UserContributions', {
                  contributions: inbound.challenge.contributions,
                  title: t('Inbound A/R Inquiry'),
                })
              )
            }}
          >
            <Text>{inbound.challenge.percentage}</Text>
          </TouchableRipple>
          <TouchableRipple
            style={[styles.valueCol, { backgroundColor: theme.colors.edit50 }]}
            onPress={() => {
              authAction(navigation, () =>
                navigation.navigate('UserContributions', {
                  contributions: inbound.edit.contributions,
                  title: t('Inbound A/R Inquiry'),
                })
              )
            }}
          >
            <Text>{inbound.edit.percentage}</Text>
          </TouchableRipple>
        </DataTable.Row>
        <DataTable.Row style={styles.row} pointerEvents={'box-none'}>
          <DataTable.Cell style={styles.typeCol}>
            {t('Outbound A/R')}
          </DataTable.Cell>
          <TouchableRipple
            style={[
              styles.valueCol,
              { backgroundColor: theme.colors.inquiry50 },
            ]}
            onPress={() => {
              authAction(navigation, () =>
                navigation.navigate('UserContributions', {
                  contributions: outbound.inquiry.contributions,
                  title: t('Inbound A/R Inquiry'),
                })
              )
            }}
          >
            <Text>{outbound.inquiry.percentage}</Text>
          </TouchableRipple>
          <TouchableRipple
            style={[
              styles.valueCol,
              { backgroundColor: theme.colors.challenge50 },
            ]}
            onPress={() => {
              authAction(navigation, () =>
                navigation.navigate('UserContributions', {
                  contributions: outbound.challenge.contributions,
                  title: t('Inbound A/R Inquiry'),
                })
              )
            }}
          >
            <Text>{outbound.challenge.percentage}</Text>
          </TouchableRipple>
          <TouchableRipple
            style={[styles.valueCol, { backgroundColor: theme.colors.edit50 }]}
            onPress={() => {
              authAction(navigation, () =>
                navigation.navigate('UserContributions', {
                  contributions: outbound.edit.contributions,
                  title: t('Inbound A/R Inquiry'),
                })
              )
            }}
          >
            <Text>{outbound.edit.percentage}</Text>
          </TouchableRipple>
        </DataTable.Row>
      </>
    )
  }, [inboundOutbound, loading, error])
  return (
    <View style={props.style}>
      <Row>
        <Text variant={'titleMedium'}>{t('user-statistics')}</Text>
      </Row>
      <Row
        gap={10}
        alignItems="center"
        justifyContent="space-between"
        style={{ marginVertical: theme.spacing(2) }}
      >
        <CoRankLabel corank={inboundOutbound?.corank} showBorder />
        <View style={styles.statItem}>
          <IconPerspective size={20} color={theme.colors.primary} />
          <Text variant={'titleMedium'} style={{ color: theme.colors.primary }}>
            {totalPerspectives}
          </Text>
        </View>
        <View style={styles.statItem}>
          <IconContributions size={20} color={theme.colors.primary} />
          <Text variant={'titleMedium'} style={{ color: theme.colors.primary }}>
            {totalContributions}
          </Text>
        </View>
      </Row>
      <TitledTable
        headerStyle={styles.row}
        header={
          <>
            <DataTable.Title style={styles.typeCol}>
              {t('Type')}
            </DataTable.Title>
            <DataTable.Title style={styles.valueCol}>
              <Icon source={Icon3dInquiry} size={18} />
            </DataTable.Title>
            <DataTable.Title style={styles.valueCol}>
              <Icon source={Icon3dChallenge} size={18} />
            </DataTable.Title>
            <DataTable.Title style={styles.valueCol}>
              <Icon source={Icon3dEdit} size={18} />
            </DataTable.Title>
          </>
        }
      >
        {content}
      </TitledTable>
    </View>
  )
}
